<template>
  <transition name="modal-fade">
    <div v-if="visible" class="dcm-overlay">
      <div class="dcm-modal">
        <!-- Header fijo -->
        <div class="dcm-header">
          <div class="dcm-header-left">
            <button class="dcm-back" @click="close">
              <i class="el-icon-arrow-left"></i>
            </button>
            <h5 class="dcm-title">Clientes dormidos</h5>
            <span v-if="clients.length" class="dcm-count-badge">{{
              clients.length
            }}</span>
          </div>
          <button class="dcm-close" @click="close">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- Filtro -->
        <div class="dcm-filters">
          <el-input
            v-model="search"
            placeholder="Buscar por nombre..."
            suffix-icon="el-icon-search"
            size="small"
            clearable
            class="dcm-search"
            @input="onSearch"
          />
        </div>

        <!-- Contenido con scroll -->
        <div class="dcm-body">
          <!-- Skeleton -->
          <div v-if="loading" class="dcm-skeleton">
            <div v-for="n in 5" :key="n" class="dcm-skeleton-row">
              <div class="sk-line w30"></div>
              <div class="sk-line w20"></div>
              <div class="sk-line w15"></div>
              <div class="sk-line w15"></div>
            </div>
          </div>

          <!-- Lista -->
          <template v-else-if="filteredClients.length">
            <div class="dcm-table-header">
              <el-checkbox
                :indeterminate="isIndeterminate"
                :value="allSelected"
                @change="toggleSelectAll"
              />
              <span class="dcm-col-name">Cliente</span>
              <span class="dcm-col-invoice">Ultima factura</span>
              <span class="dcm-col-total">Total historico</span>
              <span class="dcm-col-days">Dias inactivo</span>
              <span class="dcm-col-actions">Acciones</span>
            </div>
            <div
              v-for="client in filteredClients"
              :key="client.id"
              :class="[
                'dcm-table-row',
                { 'is-selected': selectedIds.includes(client.id) }
              ]"
            >
              <el-checkbox
                :value="selectedIds.includes(client.id)"
                @change="v => toggleSelect(client.id, v)"
              />
              <div class="dcm-col-name">
                <a
                  class="dcm-client-name dcm-client-link"
                  @click="openClientCRM(client)"
                  >{{ client.name }}</a
                >
                <span class="dcm-client-meta"
                  >{{ client.invoice_count }} facturas</span
                >
              </div>
              <div class="dcm-col-invoice">
                <span
                  v-if="client.last_invoice_date"
                  class="dcm-invoice-date"
                  >{{ formatDate(client.last_invoice_date) }}</span
                >
                <span
                  v-if="client.last_invoice_total"
                  class="dcm-invoice-amount"
                  >${{ formatMoney(client.last_invoice_total) }}</span
                >
                <span v-else class="dcm-no-data">Sin registro</span>
              </div>
              <span class="dcm-col-total dcm-total-text"
                >${{ formatMoney(client.total_historical) }}</span
              >
              <span class="dcm-col-days">
                <span
                  :class="['dcm-days-badge', daysClass(client.days_inactive)]"
                >
                  {{ client.days_inactive }} dias
                </span>
              </span>
              <div class="dcm-col-actions">
                <el-tooltip
                  content="Enviar mensaje"
                  placement="top"
                  :open-delay="300"
                >
                  <button
                    class="row-icon-btn row-icon-btn--teal"
                    @click="sendMessage(client)"
                  >
                    <i class="el-icon-chat-dot-round"></i>
                  </button>
                </el-tooltip>
                <el-tooltip
                  content="Quitar del listado"
                  placement="top"
                  :open-delay="300"
                >
                  <button
                    class="row-icon-btn row-icon-btn--danger"
                    @click="removeFromList(client)"
                  >
                    <i class="el-icon-minus"></i>
                  </button>
                </el-tooltip>
              </div>
            </div>
          </template>

          <!-- Vacio -->
          <div v-else class="dcm-empty">
            <i class="el-icon-user"></i>
            <span>No hay clientes dormidos</span>
          </div>
        </div>

        <!-- Barra de acciones masivas fija abajo -->
        <transition name="toolbar-slide">
          <div v-if="selectedIds.length" class="dcm-bulk-bar">
            <span class="dcm-bulk-info">
              {{ selectedIds.length }} cliente{{
                selectedIds.length !== 1 ? "s" : ""
              }}
              seleccionado{{ selectedIds.length !== 1 ? "s" : "" }}
            </span>
            <div class="dcm-bulk-actions">
              <el-button
                size="small"
                class="dcm-bulk-btn"
                @click="bulkSendMessage"
              >
                <i class="el-icon-chat-dot-round"></i> Enviar mensaje ({{
                  selectedIds.length
                }})
              </el-button>
              <el-button
                size="small"
                class="dcm-bulk-btn-secondary"
                @click="bulkRemove"
              >
                Quitar del listado ({{ selectedIds.length }})
              </el-button>
            </div>
          </div>
        </transition>
      </div>

      <!-- CRM Drawer (se abre encima del modal) -->
      <ClientCRMDrawer
        :visible.sync="showCRMDrawer"
        :clientData="selectedClient"
        @send-message="onCRMSendMessage"
      />
    </div>
  </transition>
</template>

<script>
import dashboardService from "../services/dashboardService";
import batchActionsMixin from "@/assets/mixins/batchActionsMixin";
import ClientCRMDrawer from "./ClientCRMDrawer";

export default {
  name: "DormantClientsModal",
  mixins: [batchActionsMixin],
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      clients: [],
      search: "",
      searchTimeout: null,
      hiddenIds: [],
      selectedIds: [],
      showCRMDrawer: false,
      selectedClient: null
    };
  },
  components: {
    ClientCRMDrawer
  },
  computed: {
    filteredClients() {
      let list = this.clients.filter(c => !this.hiddenIds.includes(c.id));
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(c => (c.name || "").toLowerCase().includes(q));
      }
      return list
        .slice()
        .sort((a, b) => (b.total_historical || 0) - (a.total_historical || 0));
    },
    allSelected() {
      return (
        this.filteredClients.length > 0 &&
        this.filteredClients.every(c => this.selectedIds.includes(c.id))
      );
    },
    isIndeterminate() {
      const count = this.filteredClients.filter(c =>
        this.selectedIds.includes(c.id)
      ).length;
      return count > 0 && count < this.filteredClients.length;
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadHidden();
        this.loadClients();
        document.addEventListener("keydown", this.onEscape);
      } else {
        document.removeEventListener("keydown", this.onEscape);
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onEscape);
  },
  methods: {
    async loadClients() {
      this.loading = true;
      this.selectedIds = [];
      try {
        const res = await dashboardService.getDormantClients({
          params: { search: this.search }
        });
        if (res.data && res.data.success) {
          this.clients = res.data.data || [];
        } else {
          this.clients = [];
          if (res.data && !res.data.success) {
            throw new Error(res.data.message || "Error al cargar");
          }
        }
      } catch (e) {
        this.clients = [];
        this.$message.error("Error al cargar clientes dormidos");
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadClients();
      }, 400);
    },
    toggleSelectAll(val) {
      if (val) {
        const ids = this.filteredClients.map(c => c.id);
        this.selectedIds = [...new Set([...this.selectedIds, ...ids])];
      } else {
        const filteredIds = this.filteredClients.map(c => c.id);
        this.selectedIds = this.selectedIds.filter(
          id => !filteredIds.includes(id)
        );
      }
    },
    toggleSelect(id, val) {
      if (val) {
        if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
      } else {
        this.selectedIds = this.selectedIds.filter(sid => sid !== id);
      }
    },
    sendMessage(client) {
      this.$emit("send-message", {
        recipients: [
          {
            id: client.id,
            name: client.name,
            phone: client.phone || null,
            email: client.email || null
          }
        ],
        context: "dormant"
      });
    },
    bulkSendMessage() {
      const selected = this.filteredClients.filter(c =>
        this.selectedIds.includes(c.id)
      );
      this.$emit("send-message", {
        recipients: selected.map(c => ({
          id: c.id,
          name: c.name,
          phone: c.phone || null,
          email: c.email || null
        })),
        context: "dormant"
      });
    },
    async bulkRemove() {
      const count = this.selectedIds.length;
      try {
        await this.$confirm(
          `Vas a quitar ${count} cliente${
            count !== 1 ? "s" : ""
          } del listado por 30 dias. Confirmar?`,
          "Confirmar accion",
          {
            confirmButtonText: "Si, continuar",
            cancelButtonText: "Cancelar",
            type: "warning"
          }
        );

        // Usar batch processing para quitar múltiples clientes
        await this.executeBatchAction({
          title: "Quitando clientes del listado",
          items: this.selectedIds,
          processor: batch => {
            return new Promise(resolve => {
              const clientsToRemove = this.filteredClients.filter(c =>
                batch.includes(c.id)
              );
              clientsToRemove.forEach(c => this.removeFromList(c));
              resolve();
            });
          },
          batchSize: 5,
          onSuccess: () => {
            this.selectedIds = [];
            this.$message.success(
              `${count} cliente${count !== 1 ? "s" : ""} quitado${
                count !== 1 ? "s" : ""
              } del listado por 30 dias`
            );
          }
        });
      } catch {
        // Cancelado
      }
    },
    openClientCRM(client) {
      this.selectedClient = client;
      this.showCRMDrawer = true;
    },
    onCRMSendMessage(payload) {
      this.$emit("send-message", payload);
    },
    removeFromList(client) {
      this.hiddenIds = [...this.hiddenIds, client.id];
      this.$message.success(`${client.name} quitado del listado por 30 dias`);
      try {
        const key =
          "dormant_hidden_" +
          (JSON.parse(localStorage.getItem("user") || "{}").business_id ||
            "default");
        const stored = JSON.parse(localStorage.getItem(key) || "[]");
        const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
        const updated = stored.filter(
          e => e.id !== client.id && e.expiry > Date.now()
        );
        updated.push({ id: client.id, expiry });
        localStorage.setItem(key, JSON.stringify(updated));
      } catch {
        // ignorar
      }
    },
    loadHidden() {
      try {
        const key =
          "dormant_hidden_" +
          (JSON.parse(localStorage.getItem("user") || "{}").business_id ||
            "default");
        const stored = JSON.parse(localStorage.getItem(key) || "[]");
        this.hiddenIds = stored
          .filter(e => e.expiry > Date.now())
          .map(e => e.id);
      } catch {
        this.hiddenIds = [];
      }
    },
    daysClass(days) {
      if (days >= 180) return "days-red";
      if (days >= 120) return "days-amber";
      return "days-yellow";
    },
    formatMoney(val) {
      return new Intl.NumberFormat("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(val || 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("es-EC");
    },
    close() {
      this.$emit("update:visible", false);
    },
    onEscape(e) {
      if (e.key === "Escape") this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3ecfb4;
$border: #e4eaf6;
$bg: #f7f8f9;

.dcm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 2000;
  display: flex;
}
.dcm-modal {
  background: #fff;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dcm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}
.dcm-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dcm-back {
  width: 36px;
  height: 36px;
  border: none;
  background: #f0f1f5;
  border-radius: 8px;
  color: $primary;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover {
    background: #e4eaf6;
  }
}
.dcm-title {
  font-size: 16px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}
.dcm-count-badge {
  background: #ba7517;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.dcm-close {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #909399;
  transition: all 0.15s;
  &:hover {
    background: $bg;
    color: $primary;
  }
}
.dcm-filters {
  padding: 14px 24px;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}
.dcm-search {
  max-width: 320px;
}
.dcm-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}
.dcm-table-header {
  display: grid;
  grid-template-columns: 32px 1fr 160px 120px 110px 80px;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: #fff;
  border-bottom: 2px solid $border;
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  position: sticky;
  top: 0;
  z-index: 5;
}
.dcm-table-row {
  display: grid;
  grid-template-columns: 32px 1fr 160px 120px 110px 80px;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-bottom: 1px solid $border;
  font-size: 12px;
  transition: background 0.12s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: $bg;
  }
}
.dcm-col-name {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.dcm-client-name {
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dcm-client-link {
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: $accent;
  }
}
.dcm-client-meta {
  font-size: 11px;
  color: #909399;
}
.dcm-col-invoice {
  display: flex;
  flex-direction: column;
}
.dcm-invoice-date {
  font-size: 11px;
  color: #606266;
}
.dcm-invoice-amount {
  font-size: 12px;
  font-weight: 600;
  color: $primary;
}
.dcm-no-data {
  color: #c0c4cc;
  font-size: 11px;
}
.dcm-total-text {
  font-weight: 700;
  color: $primary;
}
.dcm-days-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  &.days-red {
    background: #fff0f0;
    color: #e24b4a;
  }
  &.days-amber {
    background: #fff8e6;
    color: #ba7517;
  }
  &.days-yellow {
    background: #fefce8;
    color: #b45309;
  }
}
.dcm-col-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

// Icon-only action buttons
.row-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
  flex-shrink: 0;

  &--teal {
    color: $accent;
    &:hover {
      background: rgba($accent, 0.08);
      border-color: $accent;
    }
  }
  &--danger {
    color: #909399;
    &:hover {
      background: #fff0f0;
      border-color: #e24b4a;
      color: #e24b4a;
    }
  }
}
.dcm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #c0c4cc;
  gap: 10px;
  i {
    font-size: 36px;
    color: $accent;
  }
  span {
    font-size: 13px;
    font-weight: 600;
    color: #909399;
  }
}

// Skeleton
.dcm-skeleton {
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.dcm-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.sk-line {
  height: 12px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w15 {
    width: 15%;
  }
  &.w20 {
    width: 20%;
  }
  &.w30 {
    width: 30%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Transition
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s;
  .dcm-modal {
    transition: transform 0.25s;
  }
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
  .dcm-modal {
    transform: translateY(20px);
  }
}

// Selected row
.dcm-table-row.is-selected {
  background: rgba($accent, 0.05);
}

// Bulk action bar
.dcm-bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #fff;
  border-top: 1px solid $border;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 10px;
}
.dcm-bulk-info {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}
.dcm-bulk-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.dcm-bulk-btn {
  font-size: 12px;
  background: $accent;
  border: 1px solid $accent;
  color: #fff;
  border-radius: 8px;
  &:hover,
  &:focus {
    background: darken($accent, 5%);
    border-color: darken($accent, 5%);
    color: #fff;
  }
}
.dcm-bulk-btn-secondary {
  font-size: 12px;
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;
  border-radius: 8px;
  &:hover,
  &:focus {
    border-color: #909399;
    color: $primary;
  }
}

// Toolbar transition
.toolbar-slide-enter-active,
.toolbar-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.toolbar-slide-enter,
.toolbar-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (max-width: 768px) {
  .dcm-table-header,
  .dcm-table-row {
    grid-template-columns: 32px 1fr 100px 120px;
  }
  .dcm-col-days,
  .dcm-col-invoice {
    display: none;
  }
  .dcm-col-actions {
    flex-direction: row;
    gap: 6px;
    align-items: center;
  }
  .dcm-bulk-bar {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  .dcm-bulk-actions {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
